import { useContext } from 'react';
import {
  AnchoredPopover,
  AnchoredPopoverContent,
  AnchoredPopoverHeader,
  AnchoredPopoverTitle,
  AnchoredPopoverTrigger,
  Sparkline,
  StatusChip,
} from '@scripsteam/scrips-signal-ds';
import { useLabObservationTrend } from '@/patients/services/use-patient-lab-results';
import { useLabUserNames } from '@/patients/services/use-lab-user-names';
import { versionActorText, type LabAnalyteVersion } from '@/patients/lib/lab-provenance';
import { LabResultsContext } from './lab-results-context';
import {
  shouldShowSparkline,
  trendSeries,
  type LabAnalyteRow,
  type LabFlagMeta,
} from '@/patients/lib/lab-results';

interface LabAnalyteRowProps {
  patientId: string;
  analyte: LabAnalyteRow;
  /** Indent under a panel test's label row. */
  nested?: boolean;
}

/** One analyte: display · value · unit · range · flag · trend (PROD-2643). */
export function LabAnalyteRowView({ patientId, analyte, nested = false }: LabAnalyteRowProps) {
  const critical = analyte.flag?.critical === true;
  return (
    <tr
      className="border-t border-border-default"
      data-testid="lab-analyte-row"
      data-critical={critical ? 'true' : undefined}
      data-abnormal={analyte.flag && !critical ? 'true' : undefined}
    >
      <td data-nested={nested ? 'true' : undefined} className={`px-3 py-2 align-top ${nested ? 'pl-8' : ''}`}>
        <span className="text-text-primary">{analyte.display}</span>
        {analyte.preliminary ? <Marker>Preliminary</Marker> : null}
        {analyte.amended || analyte.history ? <Marker>Amended</Marker> : null}
      </td>
      <td
        data-slot="lab-card-value"
        data-critical={critical ? 'true' : undefined}
        data-abnormal={analyte.flag && !critical ? 'true' : undefined}
        className={`px-3 py-2 align-top tabular-nums ${
          critical ? 'font-bold text-status-error' : 'font-medium text-text-primary'
        }`}
      >
        {analyte.history ? <HistoryValue analyte={analyte} versions={analyte.history} critical={critical} /> : analyte.value}
      </td>
      <td className="px-3 py-2 align-top text-text-secondary">{analyte.unit || '—'}</td>
      <td className="px-3 py-2 align-top text-text-secondary tabular-nums">{analyte.range}</td>
      <td className="px-3 py-2 align-top">
        <FlagChip flag={analyte.flag} />
      </td>
      <td className="px-3 py-2 align-top">
        <LabTrend patientId={patientId} analyte={analyte} />
      </td>
    </tr>
  );
}

/**
 * An abnormal result reads an arrow and the flag in amber text, red when critical (design PaLabRequests `.flag`, 21-10);
 * a normal result has no flag. Look: showcase-patient.css (`lab-card-flag`).
 */
export function FlagChip({ flag }: { flag: LabFlagMeta | null }) {
  if (!flag) return null;
  const low = flag.code === 'L' || flag.code === 'LL';
  return (
    <span
      data-slot="lab-card-flag"
      data-testid="lab-flag"
      data-critical={flag.critical ? 'true' : undefined}
      title={flag.label}
      aria-label={flag.label}
    >
      <span aria-hidden="true">{low ? '▼' : '▲'}</span>
      {flag.label}
    </span>
  );
}

/** The footer rule of the design, carried on the line itself. */
const TREND_RULE = 'Values from different labs, units or ranges are not joined.';

/**
 * Sparkline from the patient's history for this analyte's code, at 2+ points. A point joins only
 * when its unit, performing lab and range regime all match this row (`trendSeries`).
 */
function LabTrend({ patientId, analyte }: { patientId: string; analyte: LabAnalyteRow }) {
  const { labsByRoot } = useContext(LabResultsContext);
  const { data } = useLabObservationTrend(patientId, analyte.trendKey);
  if (!analyte.trendKey || !data || data.storeAvailable === false) return null;
  const series = trendSeries(data.observations, analyte, labsByRoot);
  if (!shouldShowSparkline(series)) return null;
  // Every joined point shares this row's range, so one band is right for all of them.
  const band =
    analyte.refLow !== null && analyte.refHigh !== null
      ? { low: analyte.refLow, high: analyte.refHigh }
      : undefined;
  return (
    <span data-testid="lab-sparkline" className="inline-block" title={TREND_RULE}>
      <Sparkline
        data={series}
        width={56}
        height={14}
        band={band}
        domain={band ? { low: Math.min(band.low, ...series), high: Math.max(band.high, ...series) } : undefined}
        emphasizeLast
        aria-label={`${analyte.display} trend, ${series.length} results. ${TREND_RULE}`}
      />
    </span>
  );
}

/** The value as a button that opens the analyte's version history (C2). */
function HistoryValue({
  analyte,
  versions,
  critical,
}: {
  analyte: LabAnalyteRow;
  versions: LabAnalyteVersion[];
  /** A critical value keeps the cell's red bold; the button only adds the dotted underline. */
  critical: boolean;
}) {
  const { popoverHost } = useContext(LabResultsContext);
  return (
    <AnchoredPopover>
      <AnchoredPopoverTrigger
        render={
          <button
            type="button"
            className={`cursor-pointer bg-transparent p-0 underline decoration-dotted underline-offset-2 ${
              critical ? 'font-bold text-status-error' : 'font-medium text-brand-primary'
            }`}
            aria-label={`${analyte.display} ${analyte.value}, show history`}
            data-testid="lab-history-trigger"
          />
        }
      >
        {analyte.value}
      </AnchoredPopoverTrigger>
      <AnchoredPopoverContent container={popoverHost ?? undefined} side="bottom" align="start">
        <HistoryBody analyte={analyte} versions={versions} />
      </AnchoredPopoverContent>
    </AnchoredPopover>
  );
}

/** Versions newest first: value and unit, "Version N · current", and who wrote it and when. No reason line: the API has none. */
function HistoryBody({ analyte, versions }: { analyte: LabAnalyteRow; versions: LabAnalyteVersion[] }) {
  const names = useLabUserNames(versions.map((v) => v.actor.userId));
  return (
    <div data-testid="lab-history">
      <AnchoredPopoverHeader>
        <AnchoredPopoverTitle>{analyte.display} · history</AnchoredPopoverTitle>
      </AnchoredPopoverHeader>
      <ol className="m-0 flex list-none flex-col gap-3 p-3">
        {versions.map((v) => (
          <li key={v.version} data-testid="lab-history-version">
            <div className="flex flex-wrap items-center gap-2">
              <span className={v.current ? 'font-bold text-text-primary' : 'text-text-secondary line-through'}>
                {v.value}
              </span>
              <span className="text-text-secondary">{v.unit}</span>
              {v.current ? (
                <StatusChip tone="soft" semantic="info" size="xs">
                  Version {v.version} · current
                </StatusChip>
              ) : (
                <span className="text-text-secondary">Version {v.version}</span>
              )}
            </div>
            <div className="text-xs text-text-muted">
              {versionActorText(v.actor, names.get(v.actor.userId ?? ''))}
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

function Marker({ children }: { children: React.ReactNode }) {
  return (
    <span className="ml-2 align-middle">
      <StatusChip tone="soft" semantic="info" size="xs">
        {children}
      </StatusChip>
    </span>
  );
}
